// Settings drawer: a right-edge slide-out panel for scene-level options that
// aren't tree parameters. Currently holds the optional environmental backdrops
// (distant mountains + forest); add future scene toggles here.
//
// Mirrors gallery.js: owns its own open/close state and registers window.*
// handlers for the inline onclick attributes in index.html. main.js does a
// side-effect `import './settings.js'` to wire these up.

import { setMountainsVisible, setFujiVisible, setForestVisible, setPagodaVisible, setCourtyardVisible, setHouseVisible, setLanternVisible, setToriiVisible, setShortLanternVisible, setWaterCanVisible, setStoolVisible, setTeaVisible, setBambooVisible, setGravelDark, setRockTexture } from './environment.js';
import { setBarkTexture } from './bark.js';
import { setTreeSnowTexture } from './tree.js';
import { setFirefliesEnabled } from './fireflies.js';
import { setMuted, setMusicEnabled, setMusicVolume, setSfxEnabled, setSfxVolume, setAmbientEnabled, getPrefs, playSfx } from './audio.js';
import { app } from './core.js';

let isSettingsOpen = false;

// Live render quality: maps a Low/Med/High choice to the renderer's pixel ratio.
// The app boots at ratio 1 (soft on hi-DPI); Med/High render more pixels for a
// crisper image at a perf cost. Persisted locally (like audio prefs) — it's a
// device preference, never part of the share/save payload. The clip recorder
// reads getPixelRatio() as its floor, so a higher setting only improves captures.
const RESOLUTION_KEY = 'bonsai.resolution.v1';
const RESOLUTION_RATIOS = { low: 1, med: 1.5, high: 2 };
const DEFAULT_RESOLUTION = 'med';

function getStoredResolution() {
  try { return localStorage.getItem(RESOLUTION_KEY) || DEFAULT_RESOLUTION; } catch { return DEFAULT_RESOLUTION; }
}

function applyResolution(level) {
  const ratio = RESOLUTION_RATIOS[level] ?? RESOLUTION_RATIOS[DEFAULT_RESOLUTION];
  app.renderer?.setPixelRatio(ratio); // re-applies setSize internally, resizing the buffer
}

window.toggleSettings = function () {
  const drawer = document.getElementById('settingsDrawer');
  if (!drawer) return;
  isSettingsOpen = !isSettingsOpen;
  drawer.classList.toggle('open', isSettingsOpen);
};

// Close the drawer when clicking anywhere outside it. Ignores clicks on the
// gear button (its own onclick handles toggling) and clicks inside the drawer.
function initOutsideClickClose() {
  document.addEventListener('pointerdown', (e) => {
    if (!isSettingsOpen) return;
    const drawer = document.getElementById('settingsDrawer');
    const gear = document.getElementById('settings-button');
    if (drawer?.contains(e.target) || gear?.contains(e.target)) return;
    isSettingsOpen = false;
    drawer?.classList.remove('open');
  });
}

// Zen mode: strip the screen down to just the bonsai. Adds `zen-mode` to <body>
// (CSS hides every chrome element and reveals the lone #zen-exit-button) and
// closes the settings drawer first so it isn't left half-open underneath.
window.enterZenMode = function () {
  isSettingsOpen = false;
  document.getElementById('settingsDrawer')?.classList.remove('open');
  document.body.classList.add('zen-mode');
  playSfx('sparkle');
};

window.exitZenMode = function () {
  document.body.classList.remove('zen-mode');
  playSfx('sparkle');
};

// Inline SVG icons for the corner button. Unicode has no reliable "exit
// fullscreen" glyph (the window-control codepoints render as tofu on many
// systems), so we draw the corner-bracket expand/compress icons ourselves —
// no dependency, identical on every platform. Stroke inherits the button color.
const svgIcon = (paths) =>
  `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"` +
  ` stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

// Outward corner brackets (enter fullscreen).
const ENTER_ICON = svgIcon(
  '<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>' +
  '<path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>'
);
// Inward corner brackets (exit fullscreen).
const EXIT_ICON = svgIcon(
  '<path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>' +
  '<path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>'
);

// Fullscreen toggle for the corner button. Requests fullscreen on the page root
// (document.documentElement) and exits if already fullscreen.
window.toggleFullscreen = function () {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen?.();
  } else {
    document.exitFullscreen?.();
  }
};

// Keep the icon in sync with the actual fullscreen state — covers exiting via
// Esc / browser chrome, not just our button.
function refreshFullscreenButton() {
  const btn = document.getElementById('fullscreen-button');
  if (!btn) return;
  btn.innerHTML = document.fullscreenElement ? EXIT_ICON : ENTER_ICON;
}

// The Fullscreen API is unsupported on some browsers (notably iOS Safari, which
// exposes no requestFullscreen on elements). Hide the button there rather than
// showing a dead control.
function initFullscreenButton() {
  const btn = document.getElementById('fullscreen-button');
  if (!btn) return;
  const supported = !!document.documentElement.requestFullscreen;
  if (!supported) {
    btn.style.display = 'none';
    return;
  }
  document.addEventListener('fullscreenchange', refreshFullscreenButton);
  refreshFullscreenButton();
}

// Each scene toggle: its checkbox id paired with the setter it drives.
const SCENE_TOGGLES = [
  ['toggleMountains', setMountainsVisible],
  ['toggleFuji', setFujiVisible],
  ['toggleForest', setForestVisible],
  ['togglePagoda', setPagodaVisible],
  ['toggleCourtyard', setCourtyardVisible],
  ['toggleHouse', setHouseVisible],
  // Cloud drift lives on the sky (main.js owns it), reached via the app singleton.
  // Wrapped so the lookup defers to call time, after initSky has registered it.
  ['toggleClouds', (on) => app.setCloudsMoving?.(on)],
  ['toggleLantern', setLanternVisible],
  ['toggleShortLantern', setShortLanternVisible],
  ['toggleTorii', setToriiVisible],
  ['toggleWaterCan', setWaterCanVisible],
  ['toggleStool', setStoolVisible],
  ['toggleTea', setTeaVisible],
  ['toggleBamboo', setBambooVisible],
  ['toggleGravelDark', setGravelDark],
  // Fireflies (swarm in fireflies.js); still night- and season-gated in animate().
  ['toggleFireflies', setFirefliesEnabled],
  // One "Surface Textures" checkbox drives both the bark normals (bark.js) and the
  // rock normals (environment.js).
  ['toggleBarkTexture', (on) => { setBarkTexture(on); setRockTexture(on); setTreeSnowTexture(on); }],
];

// Wire the scene checkboxes once the DOM is ready (listeners only — no scene
// access here, since this runs before the scene is built).
function initSettings() {
  for (const [id, setter] of SCENE_TOGGLES) {
    const el = document.getElementById(id);
    if (el) el.addEventListener('change', (e) => setter(e.target.checked));
  }

  // Tea rests on the stool, so enabling Tea also enables the Stool — otherwise the
  // set would float. Tick the stool checkbox too (programmatic .checked doesn't
  // fire 'change', so call the setter directly) to keep save/share capturing both.
  const teaEl = document.getElementById('toggleTea');
  const stoolEl = document.getElementById('toggleStool');
  if (teaEl && stoolEl) {
    teaEl.addEventListener('change', (e) => {
      if (e.target.checked && !stoolEl.checked) {
        stoolEl.checked = true;
        setStoolVisible(true);
      }
    });
    // ...and turning the stool off pulls the tea with it, so it never floats.
    stoolEl.addEventListener('change', (e) => {
      if (!e.target.checked && teaEl.checked) {
        teaEl.checked = false;
        setTeaVisible(false);
      }
    });
  }

  // Sky preset dropdown (string value, so it lives outside the boolean SCENE_TOGGLES).
  const skySel = document.getElementById('skyPreset');
  if (skySel) skySel.addEventListener('change', (e) => app.applySkyPreset?.(e.target.value));

  // Resolution dropdown: seed it from the stored preference, then apply on change.
  const resSel = document.getElementById('resolutionPreset');
  if (resSel) {
    resSel.value = getStoredResolution();
    resSel.addEventListener('change', (e) => {
      const level = e.target.value;
      try { localStorage.setItem(RESOLUTION_KEY, level); } catch { /* quota/full — keep applying */ }
      applyResolution(level);
    });
  }

  initAudioControls();
  initFullscreenButton();
  initOutsideClickClose();
  initCollapsibleSections();
}

// Each settings section's <h3> acts as an accordion header: click to collapse/
// expand the rows beneath it (a `collapsed` class on the parent section hides
// them via CSS). Default open — nothing collapsed until the user clicks. The set
// of collapsed sections is persisted to localStorage (keyed by header text) so
// it survives reloads.
const COLLAPSED_KEY = 'bonsai.settings.collapsed.v1';

function loadCollapsedSet() {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function initCollapsibleSections() {
  const collapsed = loadCollapsedSet();
  const persist = () => {
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed])); }
    catch { /* quota/full — keep UI responsive */ }
  };

  const heads = [...document.querySelectorAll('#settingsDrawer .settings-section h3')];
  const sections = heads.map((h3) => {
    const section = h3.parentElement;
    // Wrap everything below the header in a grid (body) > overflow-hidden (inner)
    // pair so collapse can animate via grid-template-rows: 1fr -> 0fr (which, unlike
    // height, animates smoothly to/from auto content height). Done here so the HTML
    // markup stays clean.
    const body = document.createElement('div');
    body.className = 'settings-section-body';
    const inner = document.createElement('div');
    inner.className = 'settings-section-inner';
    let node = h3.nextSibling;
    while (node) {
      const next = node.nextSibling;
      inner.appendChild(node);
      node = next;
    }
    body.appendChild(inner);
    section.appendChild(body);
    return { h3, section, key: h3.textContent.trim() };
  });

  // Flip the "Collapse all" button to "Expand all" when every section is collapsed.
  const allBtn = document.getElementById('collapseAllSections');
  const refreshAllBtn = () => {
    if (!allBtn) return;
    const allCollapsed = sections.every(({ section }) => section.classList.contains('collapsed'));
    allBtn.textContent = allCollapsed ? 'Expand all' : 'Collapse all';
  };

  sections.forEach(({ h3, section, key }) => {
    if (collapsed.has(key)) section.classList.add('collapsed');
    h3.setAttribute('role', 'button');
    h3.setAttribute('tabindex', '0');
    const toggle = () => {
      const isCollapsed = section.classList.toggle('collapsed');
      if (isCollapsed) collapsed.add(key); else collapsed.delete(key);
      persist();
      refreshAllBtn();
    };
    h3.addEventListener('click', toggle);
    h3.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
  });

  if (allBtn) {
    allBtn.addEventListener('click', () => {
      // If anything is open, collapse everything; if all are collapsed, expand all.
      const expand = sections.every(({ section }) => section.classList.contains('collapsed'));
      sections.forEach(({ section, key }) => {
        section.classList.toggle('collapsed', !expand);
        if (expand) collapsed.delete(key); else collapsed.add(key);
      });
      persist();
      refreshAllBtn();
    });
  }
  refreshAllBtn();
}

// Audio controls live outside SCENE_TOGGLES on purpose: audio is a LOCAL
// preference (persisted in audio.js's own storage), so it's never captured into
// the share/save payload. We seed each control from the authoritative store
// first (so the browser-restored DOM state can't drift from it), then wire it.
function initAudioControls() {
  const bind = (id, evt, setter) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener(evt, (e) =>
      setter(evt === 'change' && el.type === 'checkbox' ? e.target.checked : e.target.value));
  };

  const p = getPrefs();
  const set = (id, prop, val) => { const el = document.getElementById(id); if (el) el[prop] = val; };
  set('toggleMute', 'checked', p.muted);
  set('toggleMusic', 'checked', p.musicEnabled);
  set('musicVolume', 'value', p.musicVolume);
  set('toggleSfx', 'checked', p.sfxEnabled);
  set('sfxVolume', 'value', p.sfxVolume);
  set('toggleAmbient', 'checked', p.ambientEnabled);

  bind('toggleMute', 'change', setMuted);
  bind('toggleMusic', 'change', setMusicEnabled);
  bind('musicVolume', 'input', setMusicVolume);
  bind('toggleSfx', 'change', setSfxEnabled);
  bind('sfxVolume', 'input', setSfxVolume);
  bind('toggleAmbient', 'change', setAmbientEnabled);
}

// Make the checkboxes authoritative once the scene exists. Browsers restore a
// checkbox's checked state across reloads, but the scene always rebuilds fresh
// (everything off) — so a restored "checked" box would show on while its model
// is absent, and the first click would just toggle it back off (the "click
// twice" bug). Applying each box's current state here syncs the scene to the UI
// and brings restored selections back without a click. Called from main.js after
// createEnvironment(), so app.scene/app.backdrops are ready.
export function syncSceneToggles() {
  for (const [id, setter] of SCENE_TOGGLES) {
    const el = document.getElementById(id);
    if (el) setter(el.checked);
  }
  // Apply the (possibly browser-restored) sky preset to the freshly built scene.
  // Instant (no tween) so a restored preset is just there on load.
  const skySel = document.getElementById('skyPreset');
  if (skySel) app.applySkyPreset?.(skySel.value, true);

  // Apply the stored render resolution now that app.renderer exists. Reads storage
  // directly (not the <select>), so it's correct regardless of init() ordering.
  applyResolution(getStoredResolution());
}

// ===========================================
// SHARE HOOKS (consumed by shareBonsai.js)
// ===========================================
// So a shared link reproduces the scenery too, not just the tree. settings owns
// "which decor is on <-> the scene"; shareBonsai composes this with the tree
// payload. Neither this module nor gallery needs to know about the other.

// Just the enabled toggle ids — absent ones default off on the recipient's fresh
// scene, so the payload stays tiny (and is self-describing / order-independent,
// surviving any future reordering of SCENE_TOGGLES).
export function captureSceneToggles() {
  return SCENE_TOGGLES
    .filter(([id]) => document.getElementById(id)?.checked)
    .map(([id]) => id);
}

// Drive both the checkboxes and the scene from an enabled-id list. Sets each
// box's checked state *and* calls its setter (like syncSceneToggles), so the
// settings drawer reflects the shared selection and there's no "click twice" bug.
export function applySceneToggles(enabledIds) {
  const on = new Set(enabledIds || []);
  for (const [id, setter] of SCENE_TOGGLES) {
    const checked = on.has(id);
    const el = document.getElementById(id);
    if (el) el.checked = checked;
    setter(checked);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSettings);
} else {
  initSettings();
}
